import { type Locale, format, isValid, parse } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { de, enUS as en, es, fr, it } from "date-fns/locale";
import i18n from "i18next";
import type { Languages } from "./i18n";

export type FormatNumberOptions = {
	maxDecimalPlaces: number;
	minDecimalPlaces: number;
	fallback: string;
	locale: string | undefined;
};

export type FormatNumber = {
	(n: number | string | null | undefined, decimalPlaces?: number): string;
	(n: number | string | null | undefined, opts: Partial<FormatNumberOptions>): string;
};

export type FormatTime = (date: Date | string | number | undefined | null, opts?: Partial<FormatTimeOptions>) => string;

export type FormatDate = (date: Date | string | number | undefined | null, opts?: Partial<FormatDateOptions>) => string;

/**
 * Intl/`toLocaleString` derives the sign from the original value, so a value that rounds to all-zero
 * digits (e.g. -0.001 at 2dp, or negative zero) is still rendered with a leading minus ("-0.00").
 * There is no locale-formatter option to suppress this, so we strip the leading sign whenever the
 * formatted output contains no nonzero digit. Matching on [1-9] is locale-robust because grouping
 * and decimal separators are never digits.
 */
function stripMeaninglessSign(formatted: string): string {
	if (/[1-9]/.test(formatted)) {
		return formatted;
	}
	return formatted.replace(/^[-−]/, "");
}

/**
 * Rounds half to even, and returns a number. Callers that need a rounded value rather than a
 * rendered one take this: `Number(formatNumberWithBankersRounding(…))` reads back a string
 * `toLocaleString` wrote for a locale, and a locale that groups with `.` or decimalises with `,`
 * gives `Number` something it parses as NaN.
 */
export function bankersRound(n: number, decimalPlaces: number): number {
	const factor = 10 ** decimalPlaces;
	const shifted = n * factor;
	const floorVal = Math.floor(shifted);
	const remainder = shifted - floorVal;
	if (Math.abs(remainder - 0.5) < 1e-10) {
		return (floorVal % 2 === 0 ? floorVal : floorVal + 1) / factor;
	}
	return Math.round(shifted) / factor;
}

function buildFormatNumberOpts(
	decimalPlacesOrOptions: number | Partial<FormatNumberOptions> | undefined,
): FormatNumberOptions {
	return typeof decimalPlacesOrOptions === "number"
		? {
				locale: i18n.language,
				fallback: "-",
				minDecimalPlaces: decimalPlacesOrOptions,
				maxDecimalPlaces: decimalPlacesOrOptions,
			}
		: {
				locale: decimalPlacesOrOptions?.locale ?? i18n.language,
				fallback: decimalPlacesOrOptions?.fallback ?? "-",
				maxDecimalPlaces: decimalPlacesOrOptions?.maxDecimalPlaces ?? 2,
				minDecimalPlaces: decimalPlacesOrOptions?.minDecimalPlaces ?? decimalPlacesOrOptions?.maxDecimalPlaces ?? 2,
			};
}

export const formatNumber: FormatNumber = (n, decimalPlacesOrOptions) => {
	const opts = buildFormatNumberOpts(decimalPlacesOrOptions);

	if (n !== null && n !== undefined && (typeof n === "number" || (typeof n === "string" && n !== ""))) {
		return stripMeaninglessSign(
			(typeof n === "number" ? n : Number(n)).toLocaleString(opts.locale, {
				maximumFractionDigits: opts.maxDecimalPlaces,
				minimumFractionDigits: opts.minDecimalPlaces,
				minimumIntegerDigits: 1,
			}),
		);
	}
	return opts.fallback;
};

export const formatNumberWithBankersRounding: FormatNumber = (n, decimalPlacesOrOptions) => {
	const opts = buildFormatNumberOpts(decimalPlacesOrOptions);

	if (n !== null && n !== undefined && (typeof n === "number" || (typeof n === "string" && n !== ""))) {
		const rounded = bankersRound(typeof n === "number" ? n : Number(n), opts.maxDecimalPlaces);
		// Every value pads to the requested precision, so the number of digits shown communicates the
		// display threshold the caller asked for.
		return stripMeaninglessSign(
			rounded.toLocaleString(opts.locale, {
				maximumFractionDigits: opts.maxDecimalPlaces,
				minimumFractionDigits: opts.maxDecimalPlaces,
				minimumIntegerDigits: 1,
			}),
		);
	}
	return opts.fallback;
};

export type FormatTimeOptions = {
	fallback: string;
	locale: string | undefined;
	timezone?: "CET" | "UTC";
};

export type FormatDateOptions = {
	fallback: string;
	locale: string | undefined;
	omitYear: boolean;
	timezone?: "CET" | "UTC";
};

export const formatDate: FormatDate = (date, opts) => {
	if (
		date !== null &&
		date !== undefined &&
		(typeof date === "object" || (typeof date === "string" && date !== "") || typeof date === "number")
	) {
		const normDate = typeof date === "object" ? date : new Date(date);
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const localizedStr = formatInTimeZone(
			normDate,
			opts?.timezone ?? timezone,
			opts?.omitYear ? "d MMM" : "d MMM yyyy",
			{
				locale: locales[opts?.locale ?? i18n.language],
			},
		);
		return localizedStr;
	}
	return opts?.fallback ?? "-";
};

export const formatTime: FormatTime = (date, opts) => {
	if (
		date !== null &&
		date !== undefined &&
		(typeof date === "object" || (typeof date === "string" && date !== "") || typeof date === "number")
	) {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		return `${formatInTimeZone(typeof date === "object" ? date : new Date(date), opts?.timezone ?? timezone, "p", {
			locale: locales[opts?.locale ?? i18n.language],
		})}`;
	}
	return opts?.fallback ?? "-";
};

export const formatDateTime: FormatDate = (date, opts) => {
	if (
		date !== null &&
		date !== undefined &&
		(typeof date === "object" || (typeof date === "string" && date !== "") || typeof date === "number")
	) {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return `${formatInTimeZone(
			typeof date === "object" ? date : new Date(date),
			opts?.timezone ?? timezone,
			"d MMM yyyy p",
			{
				locale: locales[opts?.locale ?? i18n.language],
			},
		)}`;
	}
	return opts?.fallback ?? "-";
};

export type FormatDateForInput = (date: Date | string | number | undefined | null, locale?: string) => string;

export const formatDateForInput: FormatDateForInput = (date, locale) => {
	if (
		date !== null &&
		date !== undefined &&
		(typeof date === "object" || (typeof date === "string" && date !== "") || typeof date === "number")
	) {
		const normDate = typeof date === "object" ? date : new Date(date);
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return formatInTimeZone(normDate, timezone, "P", {
			locale: locales[locale ?? i18n.language],
		});
	}
	return "";
};

export const parseDateDisplay = (str: string, locale?: string): string | null => {
	try {
		const parsed = parse(str.trim(), "d MMM yyyy", new Date(), {
			locale: locales[locale ?? i18n.language],
		});
		if (!isValid(parsed)) {
			return null;
		}
		return format(parsed, "yyyy-MM-dd");
	} catch {
		return null;
	}
};

/**
 * Parses a localized short date (the `P` format the date inputs use, e.g. `20/08/2026`) into the plain
 * calendar day the user typed, as `YYYY-MM-DD`.
 *
 * Separate from `parseDate` because that one answers with `toISOString()`, which re-expresses local
 * midnight in UTC: east of Greenwich `20/08/2026` comes back as `2026-08-19T22:00:00.000Z`, so anything
 * reading the day off the front of it is a day early. Callers that mean a calendar day — as everything
 * keyed by an `as-of` date does — want this instead.
 */
export const parseDateInputToDay = (localizedStr: string, locale?: string): string | null => {
	try {
		const parsed = parse(localizedStr.trim(), "P", new Date(), {
			locale: locales[locale ?? i18n.language],
		});
		return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
	} catch {
		return null;
	}
};

export type ParseDate = (localizedStr: string, locale?: string) => string | null;

export const parseDate: ParseDate = (localizedStr, locale) => {
	try {
		const parsed = parse(localizedStr, "P", new Date(), {
			locale: locales[locale ?? i18n.language],
		});
		if (Number.isNaN(parsed)) {
			return null;
		}
		return parsed.toISOString();
	} catch {
		return null;
	}
};

const locales: Partial<Record<string, Locale>> = {
	it,
	en,
	de,
	fr,
	es,
} satisfies Record<Languages, unknown>;

export function getLocalizedDateMask(locale?: string): string {
	const dateLocale = locales[locale ?? i18n.language];
	const pattern = dateLocale?.formatLong?.date({ width: "short" }) ?? "MM/dd/yyyy";
	return pattern.replace(/d+/g, "DD").replace(/M+/g, "MM").replace(/y+/g, "YYYY");
}
